import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { qualityApi, type QualityVisitorItem } from '../services/quality';

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function statusText(value: boolean): string {
  return value ? 'Ja' : 'Nej';
}

export function QualityVisitorsPage() {
  const toast = useToast();
  const { canWrite } = useAuth();
  const writeEnabled = canWrite('quality');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<QualityVisitorItem[]>([]);

  const [entryDate, setEntryDate] = useState(todayStr());
  const [visitorName, setVisitorName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [ackHygiene, setAckHygiene] = useState(true);
  const [ackHair, setAckHair] = useState(true);
  const [ackNoJewelry, setAckNoJewelry] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const data = await qualityApi.visitors();
      setItems(data.items);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda loggen');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    if (!writeEnabled || saving) {
      return;
    }
    if (!visitorName.trim()) {
      toast.error('Ange namn');
      return;
    }
    if (!purpose.trim()) {
      toast.error('Ange syfte');
      return;
    }

    setSaving(true);
    try {
      await qualityApi.createVisitor({
        entryDate,
        visitorName: visitorName.trim(),
        purpose: purpose.trim(),
        ackHygiene,
        ackHair,
        ackNoJewelry,
      });
      toast.success('Sign-off sparad');
      setVisitorName('');
      setPurpose('');
      setAckHygiene(true);
      setAckHair(true);
      setAckNoJewelry(true);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Link to="/quality" className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50">
          Tillbaka
        </Link>
        <div className="space-y-1">
          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Besökar-/introduktionslogg</h1>
        </div>
      </div>

      <form onSubmit={handleSave} className="rounded-[28px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
          <label className="space-y-2">
            <div className="text-sm font-semibold text-slate-700">Datum</div>
            <input
              type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
              value={entryDate}
              onChange={(event) => setEntryDate(event.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 shadow-sm outline-none transition focus:border-amber-500"
              disabled={!writeEnabled}
            />
          </label>

          <label className="space-y-2">
            <div className="text-sm font-semibold text-slate-700">Namn</div>
            <input
              value={visitorName}
              onChange={(event) => setVisitorName(event.target.value)}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 shadow-sm outline-none transition focus:border-amber-500"
              placeholder="Besökare eller medhjälpare"
              disabled={!writeEnabled}
            />
          </label>
        </div>

        <label className="mt-4 block space-y-2">
          <div className="text-sm font-semibold text-slate-700">Syfte eller aktivitet</div>
          <input
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 shadow-sm outline-none transition focus:border-amber-500"
            placeholder="Kort beskrivning"
            disabled={!writeEnabled}
          />
        </label>

        <div className="mt-4 flex flex-wrap gap-5 text-sm text-slate-700">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={ackHygiene} onChange={(event) => setAckHygiene(event.target.checked)} disabled={!writeEnabled} />
            <span>Hygienregler genomgångna</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={ackHair} onChange={(event) => setAckHair(event.target.checked)} disabled={!writeEnabled} />
            <span>Hårskydd vid öppen honung</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={ackNoJewelry} onChange={(event) => setAckNoJewelry(event.target.checked)} disabled={!writeEnabled} />
            <span>Inga smycken eller klocka</span>
          </label>
        </div>

        {writeEnabled ? (
          <div className="mt-5 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Sparar...' : 'Spara sign-off'}
            </button>
          </div>
        ) : null}
      </form>

      <div className="overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <div className="max-h-[calc(100dvh-360px)] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-slate-300 text-left text-slate-900">
                <th className="px-4 py-3 font-semibold">Datum</th>
                <th className="px-4 py-3 font-semibold">Namn</th>
                <th className="px-4 py-3 font-semibold">Syfte</th>
                <th className="px-4 py-3 font-semibold">Hygien</th>
                <th className="px-4 py-3 font-semibold">Hårskydd</th>
                <th className="px-4 py-3 font-semibold">Smycken</th>
                <th className="px-4 py-3 font-semibold">Signerad av</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">Laddar logg...</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">Inga poster ännu.</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-b border-slate-200 last:border-b-0">
                    <td className="px-4 py-3 text-slate-700">{item.entryDate}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{item.visitorName}</td>
                    <td className="px-4 py-3 text-slate-800">{item.purpose}</td>
                    <td className="px-4 py-3 text-slate-700">{statusText(item.ackHygiene)}</td>
                    <td className="px-4 py-3 text-slate-700">{statusText(item.ackHair)}</td>
                    <td className="px-4 py-3 text-slate-700">{statusText(item.ackNoJewelry)}</td>
                    <td className="px-4 py-3 text-slate-500">{item.createdByName ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
